/// <reference path="interfaces.ts" />

/**
 * Shared state behind the dashboard's filter-value dropdowns (the global filter pane and the
 * table widget's column filter popovers), so both controls behave identically.
 *
 * Option lists are cached in memory per key (page lifetime only — never survives a reload,
 * and selecting or applying values never invalidates an entry). A list is COMPLETE when its
 * unsearched fetch came back below the server's cap: searches then narrow the cached list
 * locally with no round-trip until the entry expires. A truncated list only serves the
 * unsearched render; its searches keep going to the server (the caller debounces).
 *
 * Selections are stored per key, independent of what the current option list displays and
 * independent of the cache's TTL — only de-selecting or an explicit reset removes entries.
 * A search filters what is SHOWN but never mutates the selection, and the caller renders
 * checkboxes from the stored set.
 */
namespace distill.dashboards.filterOptionsStore {
    /** One filter-value option. `display` is optional — the table widget computes it
     *  client-side from the column's formatting; the pane takes it from the server. */
    export interface IFilterValueOption {
        value: string;
        display?: string;
        count?: number;
    }

    export interface ISelectionEntry {
        display: string;
        count: number;
    }

    const OPTIONS_CACHE_TTL_MS = 2 * 60 * 1000;
    interface CacheEntry {
        options: IFilterValueOption[];
        complete: boolean;
        fetchedAt: number;
    }
    const _cache = new Map<string, CacheEntry>();
    const _selections = new Map<string, Map<string, ISelectionEntry>>();

    /**
     * Returns the cached option list for key, narrowed locally when the entry is complete,
     * or null when the caller must fetch (no entry, expired, or the list was truncated —
     * values exist beyond the page only the server can reach).
     */
    export function getCached(key: string, search: string): IFilterValueOption[] | null {
        const cached = _cache.get(key);
        if (!cached || Date.now() - cached.fetchedAt >= OPTIONS_CACHE_TTL_MS)
            return null;
        if (search === '')
            return cached.options;
        if (!cached.complete)
            return null;
        const term = search.toLowerCase();
        return cached.options.filter(o => (o.display || o.value).toLowerCase().includes(term));
    }

    /** Caches a fetched list. Only an unsearched response characterises the full list: below the cap it is complete
     *  (searches narrow locally), at the cap it was truncated. The blank is not a value the cap applies to. */
    export function setCached(key: string, options: IFilterValueOption[]): void {
        const valueCount = options.filter(o => o.value !== '').length;
        _cache.set(key, { options: options, complete: valueCount < distill.dashboards.FILTER_OPTIONS_LIMIT, fetchedAt: Date.now() });
    }

    /** Drops every cache entry whose key starts with prefix — used when the context an entry
     *  was fetched under (e.g. the applied global filters) changes. Selections are untouched. */
    export function clearCaches(prefix: string): void {
        _cache.forEach((_, key) => {
            if (key.startsWith(prefix))
                _cache.delete(key);
        });
    }

    /** The selection set for key — existing or new empty; the caller seeds it on first use. */
    export function getSelections(key: string): Map<string, ISelectionEntry> {
        let selections = _selections.get(key);
        if (!selections) {
            selections = new Map<string, ISelectionEntry>();
            _selections.set(key, selections);
        }
        return selections;
    }

    /** Whether a selection set already exists for key (guards first-use seeding). */
    export function hasSelections(key: string): boolean {
        return _selections.has(key);
    }

    /** Seeds a fresh selection set with values that have not been rendered yet (their display
     *  label falls back to the raw value until an option list provides it). */
    export function seedSelections(key: string, values: string[]): void {
        _selections.set(key, new Map<string, ISelectionEntry>(values.map(v => [v, { display: v, count: 0 }])));
    }

    /** Drops the selection set for key (explicit reset only — nothing expires it). */
    export function deleteSelections(key: string): void {
        _selections.delete(key);
        _validatedAt.delete(key);
    }

    /** Drops every selection set whose key starts with prefix (e.g. all of one widget's
     *  column filters when the viewer clears them all at once). */
    export function deleteSelectionsByPrefix(prefix: string): void {
        _selections.forEach((_, key) => {
            if (key.startsWith(prefix))
                _selections.delete(key);
        });
        _validatedAt.forEach((_, key) => {
            if (key.startsWith(prefix))
                _validatedAt.delete(key);
        });
    }

    // Revalidation generation: bumped whenever the context a selection was made under (the
    // applied global filters) changes. Selection sets are STALE until revalidated against
    // their column's fresh availability; the owning control decides when to revalidate.
    let _generation = 0;
    const _validatedAt = new Map<string, number>();

    /** Marks all selection sets stale (their availability context changed). */
    export function bumpGeneration(): void {
        _generation++;
    }

    /** Whether key's selection set has values and has not been validated since the last
     *  generation bump — i.e. the owner should check availability before trusting it. */
    export function selectionsStale(key: string): boolean {
        return getSelections(key).size > 0 && (_validatedAt.get(key) ?? -1) !== _generation;
    }

    /** Records that key's selection set was validated against the current generation. */
    export function markSelectionsValidated(key: string): void {
        _validatedAt.set(key, _generation);
    }
}
